/**
 * RBX Mobile Media Override (Dawn)
 * - Reads JSON from #RbxMobileMediaData (printed from theme.liquid on product pages)
 * - Injects a mobile-only media scroller into Dawn's media wrapper
 * - On mobile (<= 749px): shows custom media, hides Dawn gallery/thumbs
 * - On desktop (>= 750px): hides custom media, keeps Dawn normal
 */
(() => {
  'use strict';

  const MQ = window.matchMedia('(max-width: 749px)');
  const STYLE_ID = 'rbx-mm-style';
  const LIGHTBOX_ID = 'rbx-mm-lightbox';

  // If your theme changed Dawn markup, update these selectors only.
  const SEL = {
    // Only target media wrappers inside non-featured product containers
    mediaWrapper: '.product:not(.featured-product) .product__media-wrapper',
    dawnGallery: 'media-gallery, .product__media-list',
    dawnThumbs: '.thumbnail-slider, .thumbnail-list, .product__media-sublist',
  };

  // Helper to find the correct media wrappers depending on `scope`.
  function findMediaWrappers(scope) {
    const root = scope || document;

    // If we're passed an Element which itself is a .product, search within it
    // but only if it's not a featured product.
    if (root.nodeType === 1) {
      try {
        if (root.matches && root.matches('.product:not(.featured-product)')) {
          return Array.from(root.querySelectorAll('.product__media-wrapper'));
        }
      } catch (_) {}
    }

    // Fallback: query globally for non-featured product media wrappers
    return Array.from(document.querySelectorAll(SEL.mediaWrapper));
  }

  const NS = (window.RbxMobileMediaOverride = window.RbxMobileMediaOverride || {});

  function parseData(root) {
    const scope = root || document;
    const dataEl = scope.querySelector('#RbxMobileMediaData') || document.getElementById('RbxMobileMediaData');
    if (!dataEl) return { items: [], productId: '' };

    let items = [];
    try {
      items = JSON.parse(dataEl.textContent || '[]');
    } catch (_) {
      items = [];
    }

    const productId = String(dataEl.getAttribute('data-product-id') || '');
    return { items: Array.isArray(items) ? items : [], productId };
  }

  // iOS-safe scroll lock: position:fixed on body preserves scroll position
  let _scrollLockY = 0;
  function lockScroll() {
    _scrollLockY = window.scrollY;
    Object.assign(document.body.style, {
      overflow: 'hidden',
      position: 'fixed',
      top: `-${_scrollLockY}px`,
      width: '100%',
    });
  }
  function unlockScroll() {
    Object.assign(document.body.style, {
      overflow: '',
      position: '',
      top: '',
      width: '',
    });
    window.scrollTo(0, _scrollLockY);
  }

  function getLightbox() {
    let lb = document.getElementById(LIGHTBOX_ID);
    if (lb) return lb;

    lb = document.createElement('div');
    lb.id = LIGHTBOX_ID;
    lb.setAttribute('role', 'dialog');
    lb.setAttribute('aria-modal', 'true');
    lb.setAttribute('aria-label', 'Image zoom');

    lb.innerHTML = `
      <div class="rbx-mm-lightbox__overlay"></div>
      <button type="button" class="rbx-mm-lightbox__close" aria-label="Close">&#x2715;</button>
      <div class="rbx-mm-lightbox__img-wrap">
        <img class="rbx-mm-lightbox__img" src="" alt="">
      </div>
    `;

    function close() {
      lb.classList.remove('is-open');
      unlockScroll();
    }

    lb.querySelector('.rbx-mm-lightbox__overlay').addEventListener('click', close);
    lb.querySelector('.rbx-mm-lightbox__close').addEventListener('click', close);
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && lb.classList.contains('is-open')) close();
    });

    document.body.appendChild(lb);
    return lb;
  }

  function openLightbox(src, alt) {
    if (!MQ.matches) return; // desktop: do nothing
    const lb = getLightbox();
    const img = lb.querySelector('.rbx-mm-lightbox__img');
    img.src = src;
    img.alt = alt || '';
    lb.classList.add('is-open');
    lockScroll();
  }

  function init(root) {
    const scope = root || document;
    const { items, productId } = parseData(scope);
    if (!items.length) {
      applyState(MQ.matches, scope);
      return;
    }

    const wrappers = findMediaWrappers(scope);
    if (!wrappers.length) {
      applyState(MQ.matches, scope);
      return;
    }

    injectStyles();
    wrappers.forEach((wrapper) => ensureMobileGallery(wrapper, items, productId));

    // Toggle state immediately + on resize
    applyState(MQ.matches, scope);
  }

  function destroy(root) {
    const scope = root || document;
    scope.querySelectorAll('.rbx-mm').forEach((el) => el.remove());
    applyState(MQ.matches, scope);
  }

  NS.init = init;
  NS.destroy = destroy;

  init(document);
  MQ.addEventListener?.('change', (e) => applyState(e.matches, document));
  MQ.addListener?.((e) => applyState(e.matches, document)); // fallback
  document.addEventListener('bbp:mount', (e) => init(e.detail?.scope || document));

  function injectStyles() {
    if (document.getElementById(STYLE_ID)) return;

    const style = document.createElement('style');
    style.id = STYLE_ID;
    const prefixed = (sel, prefix) =>
      String(sel)
        .split(',')
        .map((s) => `${prefix} ${s.trim()}`)
        .join(', ');
    style.textContent = `
      /* ── Lightbox ── */
      #rbx-mm-lightbox{
        display:none;
        position:fixed;
        inset:0;
        z-index:99999;
        align-items:center;
        justify-content:center;
        overscroll-behavior:contain;
        -webkit-overflow-scrolling:touch;
        touch-action:none;
        padding:env(safe-area-inset-top,0px) env(safe-area-inset-right,0px) env(safe-area-inset-bottom,0px) env(safe-area-inset-left,0px);
      }
      #rbx-mm-lightbox.is-open{ display:flex; }
      #rbx-mm-lightbox .rbx-mm-lightbox__overlay{
        display:block;
        position:absolute;
        inset:0;
        background: rgba(0,0,0,.48);
      }
      .rbx-mm-lightbox__close{
        position:absolute;
        top:calc(2rem + env(safe-area-inset-top, 0px));
        right:calc(2rem + env(safe-area-inset-right, 0px));
        z-index:1;
        background-color:rgb(var(--color-background));
        border:0.1rem solid rgba(var(--color-foreground), 0.1);
        border-radius:50%;
        color:rgb(var(--color-foreground));
        font-size:1.8rem;
        line-height:1;
        display:flex;
        align-items:center;
        justify-content:center;
        cursor:pointer;
        padding:0rem;
        width:4rem;
        height:4rem;
      }
      .rbx-mm-lightbox__close:hover{
        color:rgba(var(--color-foreground), 0.9);
        border-color:rgba(var(--color-foreground), 0.3);
      }
      .rbx-mm-lightbox__img-wrap{
        position:relative;
        z-index:1;
        max-width:95vw;
        max-height:90vh;
        display:flex;
        align-items:center;
        justify-content:center;
      }
      .rbx-mm-lightbox__img{
        max-width:95vw;
        max-height:90vh;
        object-fit:contain;
        display:block;
        border-radius:4px;
      }

      /* ── Gallery ── */
      .rbx-mm{ display:none; }
      .rbx-mm__scroller{ display:block; }
      .rbx-mm__item{
        display:block;
        max-width:calc(50% - 1.2rem - 3px);
        width:100%;
        margin-left:auto;
        margin-right:auto;
      }
      .rbx-mm__item img,
      .rbx-mm__item video,
      .rbx-mm__item iframe{
        width:100%;
        height:auto;
        display:block;
      }

      @media (max-width: 749px){
        .rbx-mm-active .rbx-mm{ display:block; }
        ${prefixed(SEL.dawnGallery, '.rbx-mm-active')}{ display:none !important; }
        ${prefixed(SEL.dawnThumbs, '.rbx-mm-active')}{ display:none !important; }
      }
      @media (min-width: 750px){
        .rbx-mm{ display:none !important; }
      }
    `;
    document.head.appendChild(style);
  }

  function ensureMobileGallery(wrapper, items, productId) {
    // already injected?
    let box = wrapper.querySelector('.rbx-mm');
    if (!box) {
      box = document.createElement('div');
      box.className = 'rbx-mm';
      box.setAttribute('data-rbx-mm', 'true');

      const scroller = document.createElement('div');
      scroller.className = 'rbx-mm__scroller';
      scroller.setAttribute('role', 'region');
      scroller.setAttribute('aria-label', 'Product media');
      box.appendChild(scroller);

      // Insert before Dawn gallery if possible (feels native)
      const dawn = wrapper.querySelector(SEL.dawnGallery);
      if (dawn && dawn.parentNode) {
        dawn.parentNode.insertBefore(box, dawn);
      } else {
        wrapper.insertBefore(box, wrapper.firstChild);
      }
    }

    const scroller = box.querySelector('.rbx-mm__scroller');
    if (!scroller) return;

    const prevPid = String(scroller.dataset.productId || '');
    const shouldRebuild = productId && prevPid && productId !== prevPid;
    if (scroller.dataset.populated === 'true' && !shouldRebuild) return;
    if (shouldRebuild) {
      scroller.innerHTML = '';
      scroller.dataset.populated = 'false';
    }

    items.forEach((item) => {
      const node = buildNode(item);
      if (!node) return;

      const slide = document.createElement('div');
      slide.className = 'rbx-mm__item';
      slide.appendChild(node);
      scroller.appendChild(slide);
    });

    scroller.dataset.populated = 'true';
    scroller.dataset.productId = productId || prevPid || '';

    // Image tap → lightbox (mobile only, bound once per scroller)
    if (!scroller.dataset.rbxLightboxBound) {
      scroller.dataset.rbxLightboxBound = 'true';
      scroller.addEventListener('click', (e) => {
        if (!MQ.matches) return;
        const img = e.target.closest('.rbx-mm__item img');
        if (!img) return;
        openLightbox(img.src, img.alt);
      });
    }
  }

  function buildNode(item) {
    const type = String(item?.type || '').toLowerCase();

    if (type === 'image' && item.src) {
      const img = document.createElement('img');
      img.loading = 'lazy';
      img.decoding = 'async';
      img.src = item.src;
      img.alt = item.alt || '';
      return img;
    }

    if ((type === 'video' || type === 'file') && item.src) {
      const video = document.createElement('video');
      video.controls = true;
      video.loop = true;
      video.playsInline = true;
      video.preload = 'metadata';
      if (item.poster) video.poster = item.poster;

      const source = document.createElement('source');
      source.src = item.src;
      if (item.mime) source.type = item.mime;
      video.appendChild(source);

      // Original upload first, Shopify's copy as fallback (see snippets/rbx-product-video.liquid)
      if (item.fallback) {
        const fallback = document.createElement('source');
        fallback.src = item.fallback;
        fallback.type = 'video/mp4';
        video.appendChild(fallback);

        // Original loaded but its picture can't be decoded (e.g. HEVC): drop it and use the copy
        video.addEventListener('loadedmetadata', () => {
          if (!video.videoWidth && source.isConnected) {
            source.remove();
            video.load();
          }
        });
      }

      return video;
    }

    if (type === 'external_video' && item.host && item.external_id) {
      const iframe = document.createElement('iframe');
      iframe.loading = 'lazy';
      iframe.allow =
        'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share';
      iframe.allowFullscreen = true;
      iframe.title = item.alt || 'Video';

      const host = String(item.host).toLowerCase();
      if (host.includes('youtube')) {
        iframe.src = `https://www.youtube.com/embed/${encodeURIComponent(item.external_id)}?playsinline=1`;
      } else if (host.includes('vimeo')) {
        iframe.src = `https://player.vimeo.com/video/${encodeURIComponent(item.external_id)}`;
      } else {
        return null;
      }

      return iframe;
    }

    return null;
  }

  function applyState(isMobile, root) {
    const scope = root || document;
    const wrappers = findMediaWrappers(scope);
    if (!wrappers.length) return;

    wrappers.forEach((wrapper) => {
      const hasRbx = !!wrapper.querySelector('.rbx-mm[data-rbx-mm="true"]');
      wrapper.classList.toggle('rbx-mm-active', !!isMobile && hasRbx);
    });
  }
})();
